import { useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Chip,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  InputAdornment, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { FLEET_CHECKS, UNREPORTABLE_CHECK_COUNT, fleetCheckByKey, groupedFleetChecks } from './fleetCatalog';

// Chips shown inline before collapsing the rest behind a "+N more".
const CHIP_LIMIT = 8;

/**
 * The check catalog as a browsable dialog.
 *
 * Step 1 of the fleet reporting flow. Selection is always non-empty before a
 * report runs -- the API allows omitting checkKeys, but an unfiltered query can
 * truncate, so the UI never takes that path.
 *
 * Grouping and order come from groupedFleetChecks(), which shares its
 * subcategory grouping with the Technical Health tab: same domains, same
 * subcategory order, same checks within each. Someone reading a customer's
 * Technical Health page must be able to find a check in the same place here.
 *
 * Edits are drafted inside the dialog and committed on Apply, so a half-built
 * selection never re-queries or re-renders the page behind it.
 */
export default function CheckPicker({ value, onChange, disabled }) {
  const theme = useTheme();
  // The brand green (#8EA604). One accent for the whole tree -- the grouping is
  // what needs to stand out, not each domain from its neighbours. Green carries
  // the text, borders and rails; the heading rows themselves stay neutral.
  const green = theme.palette.primary.main;
  const headingBg = alpha(
    theme.palette.common.black,
    theme.palette.mode === 'dark' ? 0.25 : 0.04,
  );
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => new Set(value));
  const [search, setSearch] = useState('');

  const tree = useMemo(() => groupedFleetChecks(), []);

  // One lowercase haystack per check, built once: label, where it sits, and
  // where its answer comes from -- so "datto", "backups" and "patchServer" all
  // find something.
  const haystack = useMemo(() => {
    const domainLabel = Object.fromEntries(tree.map((d) => [d.key, d.label]));
    const map = {};
    for (const c of FLEET_CHECKS) {
      map[c.key] = [
        c.label, c.subcategory, domainLabel[c.domain] || c.domain,
        c.kind === 'manual' ? 'manual attestation' : `${c.auditRunId} ${c.checkKey}`,
      ].join(' ').toLowerCase();
    }
    return map;
  }, [tree]);

  // Every term must match, so "m365 backup" narrows rather than widens.
  const terms = useMemo(
    () => search.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [search],
  );

  const filtered = useMemo(() => {
    if (!terms.length) return tree;
    const hit = (c) => terms.every((t) => haystack[c.key].includes(t));
    return tree
      .map((d) => ({
        ...d,
        subcategories: d.subcategories
          .map((s) => ({ ...s, checks: s.checks.filter(hit) }))
          .filter((s) => s.checks.length > 0),
      }))
      .filter((d) => d.subcategories.length > 0);
  }, [tree, terms, haystack]);

  const matchKeys = useMemo(
    () => filtered.flatMap((d) => d.subcategories.flatMap((s) => s.checks.map((c) => c.key))),
    [filtered],
  );

  // Which domains the user has opened by hand. A search overrides this and
  // expands whatever matched; clearing the search hands control back.
  const [expanded, setExpanded] = useState(() => new Set(['infrastructure']));
  const searching = terms.length > 0;
  const isExpanded = (key) => (searching ? true : expanded.has(key));

  // Subcategories track what's been COLLAPSED rather than expanded: opening a
  // domain should show its checks, so open is the default and there are no 26
  // keys to seed.
  const [collapsedSubs, setCollapsedSubs] = useState(() => new Set());
  const subId = (domainKey, label) => `${domainKey}::${label}`;
  const toggleSub = (id) => {
    const next = new Set(collapsedSubs);
    if (next.has(id)) next.delete(id); else next.add(id);
    setCollapsedSubs(next);
  };

  // Re-seed the draft each time the dialog opens, so Cancel really discards and
  // a selection cleared from the chips outside is reflected on the next open.
  useEffect(() => {
    if (open) {
      setDraft(new Set(value));
      setSearch('');
    }
  }, [open, value]);

  const toggle = (key) => {
    const next = new Set(draft);
    if (next.has(key)) next.delete(key); else next.add(key);
    setDraft(next);
  };

  // Tri-state group control: all-selected clears the group, anything else takes
  // all of it.
  const setMany = (keys, selected) => {
    const next = new Set(draft);
    keys.forEach((k) => (selected ? next.delete(k) : next.add(k)));
    setDraft(next);
  };

  const groupState = (keys) => {
    const n = keys.filter((k) => draft.has(k)).length;
    return { checked: n > 0 && n === keys.length, indeterminate: n > 0 && n < keys.length, n };
  };

  const apply = () => {
    onChange([...draft]);
    setOpen(false);
  };

  const selectedChecks = value.map((k) => fleetCheckByKey[k]).filter(Boolean);
  const shown = selectedChecks.slice(0, CHIP_LIMIT);
  const overflow = selectedChecks.length - shown.length;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          startIcon={<AddRoundedIcon />}
          onClick={() => setOpen(true)}
          disabled={disabled}
        >
          Add checks
        </Button>

        {shown.map((c) => (
          <Chip
            key={c.key}
            size="small"
            label={c.label}
            disabled={disabled}
            onDelete={() => onChange(value.filter((k) => k !== c.key))}
          />
        ))}
        {overflow > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={`+${overflow} more`}
            onClick={() => setOpen(true)}
            disabled={disabled}
          />
        )}
        {selectedChecks.length > 0 && (
          <Button size="small" onClick={() => onChange([])} disabled={disabled}>
            Clear all
          </Button>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {value.length
          ? `${value.length} check${value.length === 1 ? '' : 's'} selected`
          : 'Pick at least one check to run a report'}
        {' · manual attestations report a recorded value, not a scored result'}
        {UNREPORTABLE_CHECK_COUNT > 0
          ? ` · ${UNREPORTABLE_CHECK_COUNT} unavailable (producer not live)`
          : ''}
      </Typography>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Add checks
          <Typography variant="body2" color="text.secondary">
            Grouped exactly as they appear on a customer&rsquo;s Technical Health tab.
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: 2, pb: 1, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={`Search ${FLEET_CHECKS.length} checks by name, domain or source`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {draft.size} of {FLEET_CHECKS.length} selected
                {searching ? ` · ${matchKeys.length} match${matchKeys.length === 1 ? '' : 'es'}` : ''}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {searching && matchKeys.length > 0 && (
                <Button size="small" onClick={() => setMany(matchKeys, false)}>
                  Select all {matchKeys.length} results
                </Button>
              )}
              {draft.size > 0 && (
                <Button size="small" color="error" onClick={() => setDraft(new Set())}>
                  Clear all
                </Button>
              )}
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                No check matches &ldquo;{search.trim()}&rdquo;.
              </Typography>
            )}

            {filtered.map((domain) => {
              const domainKeys = domain.subcategories.flatMap((s) => s.checks.map((c) => c.key));
              const dState = groupState(domainKeys);
              return (
                <Accordion
                  key={domain.key}
                  expanded={isExpanded(domain.key)}
                  onChange={() => {
                    if (searching) return; // search drives expansion while it's active
                    const next = new Set(expanded);
                    if (next.has(domain.key)) next.delete(domain.key); else next.add(domain.key);
                    setExpanded(next);
                  }}
                  disableGutters
                  elevation={0}
                  sx={{ '&:before': { display: 'none' }, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}` }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreRoundedIcon />}
                    sx={{ bgcolor: headingBg }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', pr: 1 }}>
                      <Checkbox
                        size="small"
                        checked={dState.checked}
                        indeterminate={dState.indeterminate}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => setMany(domainKeys, dState.checked)}
                      />
                      <Typography sx={{ fontWeight: 700, flex: 1 }}>{domain.label}</Typography>
                      <Typography variant="caption" sx={{ color: green, fontWeight: 700 }}>
                        {dState.n}/{domainKeys.length}
                      </Typography>
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 0 }}>
                    {domain.subcategories.map((sub) => {
                      const subKeys = sub.checks.map((c) => c.key);
                      const sState = groupState(subKeys);
                      const id = subId(domain.key, sub.label);
                      const checks = (
                        // Stepped in again, so the ladder reads
                        // domain > subcategory > check on indentation alone.
                        <Box sx={{ ml: 7.5 }}>
                          {sub.checks.map((c) => {
                            const picked = draft.has(c.key);
                            return (
                              <Stack
                                key={c.key}
                                direction="row"
                                alignItems="center"
                                onClick={() => toggle(c.key)}
                                sx={{
                                  pl: 0.5, pr: 1, cursor: 'pointer',
                                  bgcolor: picked ? alpha(green, 0.1) : 'transparent',
                                  '&:hover': { bgcolor: alpha(green, picked ? 0.16 : 0.05) },
                                }}
                              >
                                <Checkbox size="small" checked={picked} />
                                <Box sx={{ py: 0.5, minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: picked ? 600 : 400 }}>
                                    {c.label}
                                  </Typography>
                                  <Stack direction="row" alignItems="center" spacing={0.75}>
                                    <Box
                                      component="span"
                                      sx={{
                                        px: 0.6, borderRadius: 0.5,
                                        fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.4,
                                        color: 'text.secondary',
                                        bgcolor: alpha(theme.palette.text.primary, 0.08),
                                      }}
                                    >
                                      {c.kind === 'manual' ? 'MANUAL' : c.auditRunId}
                                    </Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontFamily: c.kind === 'manual' ? 'inherit' : 'monospace' }}
                                    >
                                      {c.kind === 'manual' ? 'attested value, not a scored result' : c.checkKey}
                                    </Typography>
                                  </Stack>
                                </Box>
                              </Stack>
                            );
                          })}
                        </Box>
                      );

                      // A check with no subcategory has no bar to sit under.
                      if (!sub.label) return <Box key="_ungrouped">{checks}</Box>;

                      return (
                        <Accordion
                          key={sub.label}
                          expanded={searching || !collapsedSubs.has(id)}
                          onChange={() => { if (!searching) toggleSub(id); }}
                          disableGutters
                          elevation={0}
                          square
                          // borderTOP only: between two subcategories that is
                          // exactly one line, and the first one is separated
                          // from the domain header above it.
                          sx={{
                            '&:before': { display: 'none' },
                            bgcolor: 'transparent',
                            borderTop: `1px solid ${alpha(green, 0.35)}`,
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreRoundedIcon fontSize="small" />}
                            sx={{
                              minHeight: 0,
                              pl: 5.5, // stepped in from the domain heading above it
                              bgcolor: headingBg,
                              '& .MuiAccordionSummary-content': { my: 0.75 },
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%', pr: 1 }}>
                              <Checkbox
                                size="small"
                                sx={{ p: 0.5 }}
                                checked={sState.checked}
                                indeterminate={sState.indeterminate}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => setMany(subKeys, sState.checked)}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700, color: green, flex: 1,
                                  textTransform: 'uppercase', letterSpacing: 0.5,
                                }}
                              >
                                {sub.label}
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: green }}>
                                {sState.n}/{subKeys.length}
                              </Typography>
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 0 }}>{checks}</AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={apply} disabled={draft.size === 0}>
            Apply · {draft.size} check{draft.size === 1 ? '' : 's'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
